import { isBrowser } from '@atomixdesign/rem-scaling'
import parseHtml from 'html-react-parser'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { withSimpleBoundary } from '../../boundaries/simple'
import { GlobalContext, GlobalContextType } from '../../globals/context'
import { useIsPreview, usePreviewScrollPosition } from '../../hooks/usePreview'
import { LazyComponent } from '../lazyComponent/lazyComponent'
import { HeaderFragment } from './fragments/header'
import { CookieBannerAlertsFragment } from "../CookieBannerAlerts/CookieBannerAlerts"
import { QuickExitFragment } from "./fragments/quickExit"
import { GlobalAlertTrigger } from "../GlobalAlertModal/globalAlertTrigger"

export type LayoutProps = {
  noindex?: boolean
  globals: GlobalContextType
}

const Main = withSimpleBoundary('main')

const Footer = dynamic(() => import('./fragments/footer').then(mod => mod.FooterFragment))

const removeRobotsMetaTag = (htmlString) => {
  // Remove the robots meta tag using regex
  return htmlString.replace(/<meta\s+name=["']robots["'][^>]*>/gi, '')
}

export const Layout: React.FC<PropsWithChildren<LayoutProps>> = ({ children, noindex, globals }) => {
  const [alerts, setAlerts] = useState({})
  const { seo, footer, header } = globals
  const isPreview = useIsPreview()
  const [position, setPosition] = usePreviewScrollPosition()
  const setCurrentPosition = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (isBrowser()) {
        setPosition(window.scrollY)
      }
    })
  }, [setPosition])

  useEffect(() => {
    if (isPreview) {
      window.scrollTo(0, position)
      window.addEventListener('scroll', setCurrentPosition)

      return () => {
        window.removeEventListener('scroll', setCurrentPosition)
      }
    }
  }, [isPreview, position, setCurrentPosition])

  useEffect(() => {
    fetch('/api/alerts', {
      method: 'GET',
      headers: {
        's-maxage' : '60',
      },
    }).then(res =>res.json()).then(data => setAlerts(data))
  }, [])

  // Process the meta tags - remove robots tag if noindex is true
  // SEOMatic will return a robots meta tag with content="all" as its set in Global settings
  const processedMeta = (noindex || seo.noindex)
    ? removeRobotsMetaTag(seo.meta)
    : seo.meta

  return (
    <GlobalContext.Provider value={globals}>
      <Head>
        {parseHtml(seo.title)}
        {parseHtml(seo.jsonLd)}
        {parseHtml(seo.link)}
        {parseHtml(processedMeta)}

        {(noindex || seo.noindex) && (
          <meta name="robots" content="noindex" />
        )}

        {(seo.id) && (
          <meta name="pageid" content={seo.id} />
        )}
      </Head>
      <CookieBannerAlertsFragment {...alerts} />
      <HeaderFragment { ...header } />

      <Main id="main">
        {children}
      </Main>

      <LazyComponent>
        <Footer { ...footer } />
      </LazyComponent>
      <QuickExitFragment />
      <GlobalAlertTrigger {...alerts} />
    </GlobalContext.Provider>
  )
}